import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Spinner } from '../ui/spinner';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { AlertCircle, SlidersHorizontal } from 'lucide-react';
import { configurationAPI } from '../../services/configurationService';
import { useBrand } from '../../context/BrandContext';

// Key-value configuration, scoped to the active brand: a key is unique within a brand, and what
// you see here is what scripts reach over /api/public/configuration?brand=… with a token minted
// in Settings. Every row records who set it last, through either door. Switching the brand in the
// top bar switches the rows — the header carries the brand like every other page does.

const formatDate = (value) => (value ? new Date(value).toLocaleString() : '—');

const ConfigurationPage = () => {
  const { activeBrandInfo } = useBrand();
  const brandKey = activeBrandInfo?.key;
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // The form doubles as the editor: clicking a row loads it for editing, an empty key means new.
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  // Each load is tagged; a response whose tag is stale (the brand changed again, or a save
  // refetched) is dropped, so a slow answer from the previous brand cannot overwrite rows.
  const loadID = useRef(0);

  // The brand the user is looking at right now. save's closure froze the brand the save started
  // under, so its success path compares the two: a save that outlived a brand switch must not
  // clear the form (the switch already did) nor speak its error on the new brand's page.
  const brandRef = useRef(brandKey);
  useEffect(() => {
    brandRef.current = brandKey;
  }, [brandKey]);

  const load = useCallback(async () => {
    const id = ++loadID.current;
    try {
      const { data } = await configurationAPI.list();
      if (id !== loadID.current) return;
      setConfigs(data.configs || []);
      setError('');
    } catch (err) {
      if (id !== loadID.current) return;
      setError(err.response?.data?.error || 'Failed to load configuration');
    } finally {
      if (id === loadID.current) setLoading(false);
    }
  }, [brandKey]);

  // The route does not remount on a brand switch, so reloading here is what makes the rows (and
  // the form) follow the brand — without it, a row clicked before the switch would save the old
  // brand's value under the new brand's X-Brand.
  useEffect(() => {
    setLoading(true);
    setConfigs([]);
    setKey('');
    setValue('');
    load();
  }, [load]);

  const save = async () => {
    if (saving || !key.trim()) return;
    const savedBrand = brandKey;
    setSaving(true);
    setError('');
    try {
      await configurationAPI.set({ key: key.trim(), value });
      if (brandRef.current === savedBrand) {
        setKey('');
        setValue('');
      }
      await load();
    } catch (err) {
      if (brandRef.current === savedBrand) {
        setError(err.response?.data?.error || 'Failed to save the configuration');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-5xl">
      <div>
        <h1 className="font-display text-2xl font-bold text-ink flex items-center gap-2">
          <SlidersHorizontal className="h-5 w-5 text-primary-500" /> Configuration
        </h1>
        <p className="text-sm text-ink-dim mt-1 font-medium">
          Key-value settings for <span className="text-ink font-bold">{activeBrandInfo?.display_name || activeBrandInfo?.key}</span>.
          Keys are unique per brand; an empty value clears what a key held. Scripts set and read
          the same keys over the API with a token from Settings.
        </p>
      </div>

      {error && (
        <Alert variant="error">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Set a value</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <label htmlFor="configuration-key" className="text-sm text-ink-dim font-medium">Key</label>
            <Input
              id="configuration-key"
              value={key}
              placeholder="web_domain"
              maxLength={255}
              onChange={(e) => setKey(e.target.value)}
              className="font-mono"
            />
          </div>
          <div>
            <label htmlFor="configuration-value" className="text-sm text-ink-dim font-medium">
              Value — empty clears what the key held
            </label>
            <Textarea
              id="configuration-value"
              value={value}
              placeholder="https://example.com"
              onChange={(e) => setValue(e.target.value)}
              className="font-mono text-sm"
            />
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={save} disabled={saving || !key.trim()}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
            {(key || value) && (
              <Button variant="outline" onClick={() => { setKey(''); setValue(''); }} disabled={saving}>
                Clear form
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">All keys</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Spinner />
          ) : configs.length === 0 ? (
            <p className="text-sm text-ink-dim font-medium">Nothing configured yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Key</TableHead>
                  <TableHead>Value</TableHead>
                  <TableHead>Set by</TableHead>
                  <TableHead className="text-right">Set at</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {configs.map((cfg) => (
                  <TableRow
                    key={cfg.key}
                    className="cursor-pointer"
                    onClick={() => { setKey(cfg.key); setValue(cfg.value); }}
                    title="Click to edit this key"
                  >
                    <TableCell className="font-mono text-xs text-ink">{cfg.key}</TableCell>
                    <TableCell className="font-mono text-xs text-ink-dim max-w-md truncate">
                      {cfg.value === '' ? <span className="text-ink-mute">(empty)</span> : cfg.value}
                    </TableCell>
                    <TableCell className="text-xs text-ink-dim font-medium">
                      {cfg.updated_by || '—'}
                    </TableCell>
                    <TableCell className="text-xs text-ink-mute text-right">
                      {formatDate(cfg.updated_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ConfigurationPage;
